import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from "@angular/core";
import { FormControl } from "@angular/forms";
import { Subscription } from "rxjs";
import { debounceTime } from "rxjs/operators";
import IOrgNode from "@skills-app/models/orgNode";

@Component({
  standalone: false,
  selector: "app-org-filter",
  templateUrl: "./org-filter.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ["./org-filter.css"],
})
export class OrganisationFilterComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() organisations: IOrgNode[] = [];
  @Output() filterOrganisations = new EventEmitter<IOrgNode[]>();

  public filterFormControl = new FormControl("", { nonNullable: true });

  public readonly helperText =
    "Click to browse the Line of Business tree, or start typing to narrow the filtered options.";
  public readonly typedHint =
    "Start typing to filter the Line of Business tree.";

  private inputValue?: Subscription;

  ngOnInit(): void {
    this.inputValue = this.filterFormControl.valueChanges
      .pipe(debounceTime(250))
      .subscribe((orgName) => this.filter(String(orgName ?? "").toLowerCase()));
  }

  ngOnChanges(_changes: SimpleChanges): void {
    this.filter(String(this.filterFormControl.value ?? "").toLowerCase());
  }

  ngOnDestroy(): void {
    this.inputValue?.unsubscribe();
  }

  onInputFocus(): void {
    this.filter(String(this.filterFormControl.value ?? "").toLowerCase());
  }

  clearFilter(): void {
    this.filterFormControl.setValue("", { emitEvent: false });
    this.filter("");
  }

  get trimmedValue(): string {
    return String(this.filterFormControl.value ?? "").trim();
  }

  get hasTypedFilter(): boolean {
    return this.trimmedValue.length > 0;
  }

  private filter(orgName: string): void {
    const orgs = this.getFilteredOrganisations(
      this.organisations ?? [],
      orgName,
    );
    this.filterOrganisations.emit(orgs);
  }

  private getFilteredOrganisations(
    orgs: IOrgNode[],
    orgName: string,
  ): IOrgNode[] {
    if (!orgs.length) return [];

    const trimmedFilter = String(orgName ?? "").trim();

    // Default state should keep the tree visually collapsed.
    // We still emit the full org set, but without forcing expansion.
    if (!trimmedFilter.length) {
      return (orgs ?? []).map((org) => ({
        ...org,
        isExpanded: false,
      })) as any[];
    }

    const filtered: IOrgNode[] = [];

    orgs.forEach((org) => {
      const name = (org.Name ?? "").toLowerCase();

      if (name.includes(trimmedFilter)) {
        filtered.push({ ...org, isExpanded: true } as any);
        return;
      }

      if (org.Children?.length) {
        const children = this.getFilteredOrganisations(
          org.Children,
          trimmedFilter,
        );

        if (children.length) {
          filtered.push({
            ...org,
            Children: children,
            isExpanded: true,
          } as any);
        }
      }
    });

    return filtered;
  }
}
